import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Drawer, Tabs, Button, Typography, Space, Divider, Skeleton, Row, Col, Form, Input, Card, message, Spin, Tag, Empty } from 'antd'
import {
  LeftOutlined,
  RightOutlined,
  HeartOutlined,
  MailOutlined,
  PhoneOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  BankOutlined,
  UserOutlined,
  BookOutlined,
  DownloadOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import axios from 'axios'
import { CB_CANDIDATE_SAVE, CB_SEARCH, CB_SEARCH_DETAILS, CANDIDATE_NOTES, CANDIDATE_NOTE_UPDATE, CANDIDATE_NOTE_DELETE, CANDIDATE_UPDATE_PARTIALLY, PENDING_CANDIDATE_NOTES, PENDING_CANDIDATE_NOTE_DELETE, PENDING_CANDIDATE_NOTE_ADD } from '../../../ApiUrl'
import { requestHeader } from '../../../others/header'
import DOMPurify from 'dompurify'
import Notes from '../../modules/Modals/Notes'
import { useCallAPI } from '../../../hooks/useCallAPI'
import { useSelector } from 'react-redux'

const { Title, Text } = Typography
const { TabPane } = Tabs

// Utility function to safely render any field that might be an object
const safeRenderField = (field) => {
  if (field === null || field === undefined) {
    return '';
  }
  
  if (typeof field === 'string') {
    return field;
  }
  
  if (typeof field === 'object') {
    // If it's an object with City, State, Country properties (location object)
    if (field.City || field.State || field.Country) {
      return [
        field.City, 
        field.State, 
        field.Country
      ].filter(Boolean).join(', ');
    }
    
    // For other objects, try to convert to string or return a placeholder
    try {
      return JSON.stringify(field);
    } catch (e) {
      return '[Complex Object]';
    }
  }
  
  // For other types, convert to string
  return String(field);
};

// Utility function to parse and clean resume HTML
const parseResumeHtml = (htmlString) => {
  if (!htmlString) return '';

  // First decode HTML entities
  const decoded = htmlString
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');

  // Sanitize the decoded HTML
  const clean = DOMPurify.sanitize(decoded, {
    ALLOWED_TAGS: ['div', 'p', 'span', 'b', 'i', 'u', 'a', 'br', 'ul', 'li'],
    ALLOWED_ATTR: ['style', 'href', 'class'],
  });

  return clean;
};

// Extract professional summary from resume text and format bullet points
const extractProfessionalSummary = (resumeText) => {
  if (!resumeText) return [];
  
  // Try to find a section labeled as summary or professional summary
  const summaryRegex = /(?:PROFESSIONAL\s+SUMMARY|SUMMARY)(?:\s*:|:?\s*)([\s\S]*?)(?:\r?\n\r?\n\s*[A-Z]|\r?\n\s*[A-Z][A-Z\s]+:)/i;
  const match = resumeText.match(summaryRegex);
  
  let summaryText = '';
  if (match && match[1]) {
    summaryText = match[1].trim();
  }
  
  // Split the summary into bullet points if it contains asterisks
  if (summaryText.includes('*')) {
    return summaryText.split('*')
      .map(item => item.trim())
      .filter(item => item.length > 0);
  }
  
  return [summaryText]; // Return as a single item if no bullet points
};

const ProfileSection = ({ title, children }) => (
  <div style={{ marginBottom: '24px' }}>
    <Title level={5} style={{ 
      marginBottom: '12px', 
      borderBottom: '1px solid #f0f0f0', 
      paddingBottom: '8px',
      color: '#1890ff'
    }}>
      {title}
    </Title>
    <div style={{ paddingLeft: '8px' }}>{children}</div>
  </div>
);

const WorkHistoryItem = ({ company, title, tenure }) => (
  <Card 
    size="small" 
    style={{ 
      marginBottom: '16px', 
      borderLeft: '3px solid #1890ff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.09)'
    }}
  >
    <div style={{ padding: '8px' }}>
      <Text strong style={{ fontSize: '16px', display: 'block' }}>{title}</Text>
      <Text style={{ display: 'block', color: '#555' }}>{company}</Text>
      <Text type="secondary">{tenure}</Text>
    </div>
  </Card>
);

const EducationItem = ({ school, major, degree, graduationDate }) => (
  <Card 
    size="small" 
    style={{ 
      marginBottom: '16px', 
      borderLeft: '3px solid #52c41a',
      boxShadow: '0 2px 8px rgba(0,0,0,0.09)'
    }}
  >
    <div style={{ padding: '8px' }}>
      <Text strong style={{ fontSize: '16px', display: 'block' }}>{school}</Text>
      {degree && <Text style={{ display: 'block', color: '#555' }}>{degree} {major ? `in ${major}` : ''}</Text>}
      {graduationDate && <Text type="secondary">{graduationDate}</Text>}
    </div>
  </Card>
);

export default function CandidateDetails({
  visible = false,
  onClose = () => { },
  resumeId,
  source = 'CareerBuilder',
  emailHash,
  candidateId
}) {
  const [loading, setLoading] = useState(false)
  const [candidateDetails, setCandidateDetails] = useState(null)
  const [error, setError] = useState(null)
  const [showNotes, setShowNotes] = useState(true)
  const [activeTab, setActiveTab] = useState("profile") // Default to profile tab
  const [form] = Form.useForm()
  const users = useSelector(({ users }) => users);
  
  // Log props changes for debugging
  useEffect(() => {
    console.log('CandidateDetails props changed:', { visible, resumeId, source });
  }, [visible, resumeId, source]);
  
  // Log state changes for debugging
  useEffect(() => {
    console.log('CandidateDetails state changed:', { 
      hasDetails: !!candidateDetails, 
      loading, 
      hasError: !!error 
    });
  }, [candidateDetails, loading, error]);
  
  // API hooks for notes
  const NotesData = useCallAPI(CANDIDATE_NOTES);
  const UpdateNotes = useCallAPI(CANDIDATE_NOTE_UPDATE);
  const DeleteNotes = useCallAPI(CANDIDATE_NOTE_DELETE);
  const UpdatePartially = useCallAPI(CANDIDATE_UPDATE_PARTIALLY);
  const PendingNotesData = useCallAPI(PENDING_CANDIDATE_NOTES);
  const DeletePendingNotes = useCallAPI(PENDING_CANDIDATE_NOTE_DELETE);
  const AddNote = useCallAPI(PENDING_CANDIDATE_NOTE_ADD);

  // Reset state when drawer closes
  useEffect(() => {
    if (!visible) {
      setCandidateDetails(null);
      setError(null);
      setLoading(false);
      setShowNotes(true);
      setActiveTab("profile");
    }
  }, [visible]);

  // Separate useEffect for fetching candidate details
  useEffect(() => {
    const fetchCandidateDetails = async () => {
      if (!resumeId || !visible) {
        console.log('Skipping fetch - conditions not met:', { resumeId, visible });
        return;
      }
      
      console.log('Starting to fetch candidate details for resumeId:', resumeId);
      setLoading(true);
      
      try {
        console.log('Making API request to:', `${CB_SEARCH_DETAILS}/${resumeId}`);
        const response = await axios.post(
          `${CB_SEARCH_DETAILS}/${resumeId}`,
          {},
          requestHeader()
        );
        
        console.log('API Response status:', response.status);
        console.log('API Response data:', response.data);
        
        // Check for error: false instead of success: true
        if (response.data && response.data.error === false && response.data.data) {
          console.log('Valid response received, setting candidate details');
          const candidateData = response.data.data;
          setCandidateDetails(candidateData);
          
          // Set notes data from the candidate details response
          if (candidateData.notes && candidateData.notes.length > 0) {
            console.log('Setting notes data from response, count:', candidateData.notes.length);
            NotesData.setData({
              data: {
                data: {
                  multipleNotes: candidateData.notes
                }
              }
            });
          } else {
            console.log('No notes found in candidate data');
          }
        } else {
          console.error('Invalid response format:', response.data);
          setError('Failed to fetch candidate details: Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching candidate details:', err);
        setError(`Error fetching candidate details: ${err.message}`);
      } finally {
        console.log('Fetch completed, setting loading to false');
        setLoading(false);
      }
    };

    fetchCandidateDetails();
    
    // Cleanup function to handle component unmount
    return () => {
      console.log('Cleanup: Component unmounting or dependencies changed');
    };
  }, [resumeId, visible]);

  // Function to migrate pending notes to candidate notes
  const migratePendingNotes = useCallback(async (candidateId) => {
    if (!candidateId || !resumeId && !emailHash) {
      console.log('Cannot migrate notes: missing candidateId or identifier');
      return;
    }
    
    try {
      console.log(`Attempting to migrate pending notes for ${source} candidate:`, {
        candidateId,
        resumeId,
        emailHash
      });
      
      // Determine the endpoint based on available identifiers
      let endpoint;
      if (resumeId) {
        endpoint = `migrate-notes/${source}/${resumeId}/${candidateId}`;
      } else if (emailHash) {
        endpoint = `migrate-notes/${source}/email/${emailHash}/${candidateId}`;
      }
      
      if (!endpoint) {
        console.error('Cannot create endpoint for note migration');
        return;
      }
      
      const response = await axios.post(
        `${APP_URL}${endpoint}`,
        {},
        requestHeader()
      );
      
      console.log('Note migration response:', response.data);
      
      if (response.data && response.data.error === false) {
        console.log('Notes migrated successfully');
      }
    } catch (err) {
      console.error('Error migrating notes:', err);
    }
  }, [resumeId, emailHash, source]);

  // Remove the force loading timeout as it might interfere
  useEffect(() => {
    console.log('Loading state changed:', loading);
  }, [loading]);

  // Add debug log for visible prop changes
  useEffect(() => {
    console.log('Visible prop changed:', visible);
  }, [visible]);

  // Function to get notes for this candidate
  const getNotes = (record) => {
    const candidateId = record?.candidateId || record?._id;
    
    // Skip if no candidateId, already loading, or already loaded
    if (!candidateId || loading || showNotes) return;
    
    setLoading(true);
    
    // Use the correct endpoint format for fetching notes
    NotesData.get(candidateId)
      .then((response) => {
        setShowNotes(true);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching notes:', err);
        setLoading(false);
      });
  };

  // Handle drawer close
  const handleClose = useCallback(() => {
    setLoading(false);
    onClose();
  }, [onClose]);

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
  
    if (key === "notes" && candidateDetails && !showNotes) {
      // Only fetch notes if they haven't been loaded yet
      getNotes(candidateDetails);
    }
  }, [candidateDetails, getNotes, showNotes]);

  // Use useCallback for handleNotes
  const handleNotes = useCallback((value) => {
    const candidateId = candidateDetails?.candidateId || candidateDetails?._id;
    if (candidateId) {
      UpdatePartially.patch({ multipleNotes: value }, candidateId)
        .then(() => {
          // Update the local state with the new notes
          if (candidateDetails && candidateDetails.notes) {
            setCandidateDetails({
              ...candidateDetails,
              notes: [...candidateDetails.notes, ...value]
            });
          }
        })
        .catch(err => {
          console.error('Error adding note:', err);
        });
    }
  }, [candidateDetails, UpdatePartially, getNotes]);

  // Use useCallback for handleUpdateNotes
  const handleUpdateNotes = useCallback((value, id) => {
    const candidateId = candidateDetails?.candidateId || candidateDetails?._id;
    if (candidateId) {
      UpdateNotes.patch(value, `${candidateId}/${id}`)
        .then(() => {
          // Update the local state with the updated note
          if (candidateDetails && candidateDetails.notes) {
            const updatedNotes = candidateDetails.notes.map(note => 
              note._id === id ? { ...note, ...value } : note
            );
            setCandidateDetails({
              ...candidateDetails,
              notes: updatedNotes
            });
          }
        })
        .catch(err => {
          console.error('Error updating note:', err);
        });
    }
  }, [candidateDetails, UpdateNotes, getNotes]);

  // Use useCallback for handleDeleteNotes
  const handleDeleteNotes = useCallback((id) => {
    const candidateId = candidateDetails?.candidateId || candidateDetails?._id;
    if (candidateId) {
      DeleteNotes.deletee(`${candidateId}/${id}`)
        .then(() => {
          // Update the local state by removing the deleted note
          if (candidateDetails && candidateDetails.notes) {
            const filteredNotes = candidateDetails.notes.filter(note => note._id !== id);
            setCandidateDetails({
              ...candidateDetails,
              notes: filteredNotes
            });
          }
        })
        .catch(err => {
          console.error('Error deleting note:', err);
        });
    }
  }, [candidateDetails, DeleteNotes, getNotes]);

  // Add a useEffect to log candidateDetails state changes
  useEffect(() => {
    console.log('CandidateDetails state changed:', candidateDetails ? 'Has data' : 'No data');
    if (candidateDetails) {
      console.log('CandidateDetails name:', candidateDetails.ContactName?.[0]);
    }
  }, [candidateDetails]);

  const renderResumeContent = () => {
    if (!candidateDetails) {
      return <Text>No resume content available</Text>;
    }

    // Check if we have a PDF (base64 encoded)
    if (candidateDetails.ResumeHTML?.[0]?.startsWith('JVBERi0')) {
      // This is a base64 encoded PDF
      const pdfData = candidateDetails.ResumeHTML[0];
      const pdfUrl = `data:application/pdf;base64,${pdfData}`;

      return (
        <Card
          style={{
            padding: 0,
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ width: '100%', height: '800px', position: 'relative' }}>
            <object
              data={pdfUrl}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            >
              <iframe
                src={pdfUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              >
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  margin: '20px'
                }}>
                  <Text>Your browser does not support PDFs.</Text>
                  <br />
                  <Button 
                    type="primary" 
                    icon={<DownloadOutlined />} 
                    href={pdfUrl} 
                    download="resume.pdf"
                    style={{ marginTop: '12px' }}
                  >
                    Download PDF
                  </Button>
                </div>
              </iframe>
            </object>
          </div>
        </Card>
      );
    } 
    
    // If not PDF, try to render as HTML
    if (candidateDetails.ResumeHTML?.[0]) {
      const cleanHtml = parseResumeHtml(candidateDetails.ResumeHTML[0]);
      
      return (
        <Card
          style={{
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          <div 
            className="resume-content"
            style={{ 
              padding: '16px', 
              borderRadius: '4px',
              backgroundColor: '#fff',
              maxHeight: '70vh',
              overflowY: 'auto',
              lineHeight: '1.6'
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: cleanHtml }} />
          </div>
        </Card>
      );
    }
    
    // If no HTML content, try to display the text content
    if (candidateDetails.ResumeText?.[0]) {
      return (
        <Card
          style={{
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          <div 
            className="resume-content"
            style={{ 
              padding: '16px', 
              borderRadius: '4px',
              backgroundColor: '#fff',
              maxHeight: '70vh',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '14px',
              lineHeight: '1.6'
            }}
          >
            {candidateDetails.ResumeText[0]}
          </div>
        </Card>
      );
    }
    
    return (
      <Empty 
        description="No resume content available" 
        image={Empty.PRESENTED_IMAGE_SIMPLE} 
      />
    );
  };

  // Fetch notes when component mounts or candidateDetails changes
  useEffect(() => {
    if (candidateDetails) {
      // Check if we already have notes from the search results
      if (candidateDetails?.notesInfo?.hasNotes) {
        setNotes({
          data: {
            data: {
              notes: candidateDetails.notesInfo.notes,
              fromCandidate: candidateDetails.notesInfo.fromCandidate,
              fromPending: candidateDetails.notesInfo.fromPending
            }
          }
        });
        return;
      }
      
      // Otherwise fetch notes
      getNotes(candidateDetails);
    }
  }, [candidateDetails, getNotes]);

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      size='large'
      width={800}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              {candidateDetails?.ContactName?.[0] ?? 'Candidate Details'}
            </Title>
          </Space>
        </div>
      }
      extra={
        <Space>
          <Button 
            type="primary" 
            ghost
            icon={<MailOutlined />}
            onClick={() => {
              if (candidateDetails?.ContactEmail?.[0]) {
                window.open(`mailto:${candidateDetails.ContactEmail[0]}`);
              }
            }}
            disabled={!candidateDetails?.ContactEmail?.[0]}
          >
            Email
          </Button>
        </Space>
      }
      destroyOnClose={false}
      maskClosable={false}
      headerStyle={{ 
        borderBottom: '1px solid #f0f0f0',
        padding: '16px 24px'
      }}
      bodyStyle={{ 
        padding: '24px 0',
        backgroundColor: '#f5f7fa'
      }}
      style={{
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    >
      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      ) : error ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'red' }}>
          <div>{error}</div>
          <Button 
            type="primary" 
            onClick={() => {
              setError(null);
              setLoading(true);
              // Re-fetch the data
              axios.post(
                `${CB_SEARCH_DETAILS}/${resumeId}`,
                {},
                requestHeader()
              ).then(response => {
                if (response.data && response.data.error === false && response.data.data) {
                  setCandidateDetails(response.data.data);
                } else {
                  setError('Failed to fetch candidate details');
                }
              }).catch(err => {
                setError(`Error: ${err.message}`);
              }).finally(() => {
                setLoading(false);
              });
            }}
            style={{ marginTop: '16px' }}
          >
            Retry
          </Button>
        </div>
      ) : (
        <div style={{ padding: '0 24px' }}>
          {/* Header Section */}
          <Card 
            style={{ 
              marginBottom: '24px', 
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={16}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <UserOutlined style={{ fontSize: '24px', marginRight: '12px', color: '#1890ff', backgroundColor: '#e6f7ff', padding: '8px', borderRadius: '50%' }} />
                    <div>
                      <Title level={4} style={{ margin: 0 }}>{candidateDetails?.ContactName?.[0] ?? 'N/A'}</Title>
                      <Text type="secondary">{candidateDetails?.ResumeTitle?.[0] || 'Candidate'}</Text>
                    </div>
                  </div>
                  <div style={{ marginLeft: '44px' }}>
                    <Space direction="vertical" size="small">
                      <div>
                        <EnvironmentOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        <Text>{candidateDetails?.HomeLocation?.[0]?.City ?? 'N/A'}, {candidateDetails?.HomeLocation?.[0]?.State ?? 'N/A'}</Text>
                      </div>
                      <div>
                        <MailOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        <Text>{candidateDetails?.ContactEmail?.[0] ?? 'N/A'}</Text>
                      </div>
                      <div>
                        <PhoneOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        <Text>{candidateDetails?.ContactPhone?.[0] ?? 'N/A'}</Text>
                      </div>
                    </Space>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Space>
                    {candidateDetails?.ExperienceMonths?.[0] && (
                      <Tag color="blue" style={{ padding: '4px 8px' }}>
                        {Math.floor(candidateDetails.ExperienceMonths[0] / 12)} years exp
                      </Tag>
                    )}
                    {candidateDetails?.HighestDegree?.[0] && (
                      <Tag color="green" style={{ padding: '4px 8px' }}>
                        {candidateDetails.HighestDegree[0]}
                      </Tag>
                    )}
                  </Space>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Tabs Section */}
          <Row gutter={24}>
            <Col span={24}>
              <Tabs 
                defaultActiveKey="profile" 
                activeKey={activeTab} 
                onChange={handleTabChange}
                type="card"
                style={{ 
                  backgroundColor: '#fff',
                  padding: '16px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.09)'
                }}
              >
                <TabPane 
                  tab={
                    <span>
                      <UserOutlined />
                      Profile
                    </span>
                  } 
                  key="profile"
                >
                  <div>
                    {/* Professional Summary Section */}
                    {candidateDetails?.ResumeText?.[0] && (
                      <>
                        <ProfileSection title="Professional Summary">
                          <Card
                            style={{
                              backgroundColor: '#f9f9f9',
                              borderRadius: '8px',
                              borderLeft: '4px solid #1890ff',
                              maxHeight: '300px',
                              overflow: 'auto'
                            }}
                          >
                            <div style={{ padding: '8px' }}>
                              {extractProfessionalSummary(candidateDetails.ResumeText[0]).map((item, index) => (
                                <div 
                                  key={index} 
                                  style={{ 
                                    marginBottom: '12px',
                                    display: 'flex',
                                    alignItems: 'flex-start'
                                  }}
                                >
                                  <div 
                                    style={{ 
                                      color: '#1890ff', 
                                      marginRight: '8px',
                                      fontSize: '16px',
                                      marginTop: '2px'
                                    }}
                                  >
                                    •
                                  </div>
                                  <Text style={{ 
                                    lineHeight: '1.6', 
                                    fontSize: '14px', 
                                    display: 'block',
                                    color: '#333'
                                  }}>
                                    {item}
                                  </Text>
                                </div>
                              ))}
                            </div>
                          </Card>
                        </ProfileSection>
                        <Divider style={{ margin: '16px 0' }} />
                      </>
                    )}

                    {/* Work History Section */}
                    <ProfileSection title="Work History & Experience">
                      {candidateDetails?.WorkHistory?.[0]?.ExtCompany?.map((work, index) => (
                        <WorkHistoryItem
                          key={index}
                          company={work?.CompanyName?.[0] ?? 'N/A'}
                          title={work?.JobTitle?.[0] ?? 'N/A'}
                          tenure={work?.Tenure?.[0] ?? 'N/A'}
                        />
                      )) ?? <Text type="secondary">No work history available</Text>}
                    </ProfileSection>

                    <Divider style={{ margin: '16px 0' }} />

                    {/* Education Section */}
                    <ProfileSection title="Education">
                      {candidateDetails?.EducationHistory?.[0]?.ExtSchool?.length > 0 ? (
                        candidateDetails.EducationHistory[0].ExtSchool.map((edu, index) => (
                          <EducationItem
                            key={index}
                            school={edu.SchoolName[0]}
                            major={edu.Major[0]}
                            degree={edu.Degree[0]}
                            graduationDate={edu.GraduationDate[0]?.replace(/^1/, '')} // Remove leading '1' from year
                          />
                        ))
                      ) : (
                        <Text type="secondary">No education history available</Text>
                      )}
                    </ProfileSection>

                    <Divider style={{ margin: '16px 0' }} />

                    {/* Skills Section */}
                    <ProfileSection title="Skills & Expertise">
                      <Card
                        style={{
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}
                      >
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {candidateDetails?.Interests?.[0]?.ExtInterest?.map((interest, index) => {
                            // Determine tag color based on skill category
                            let tagColor = 'blue';
                            const skill = interest.Interest[0].toLowerCase();
                            
                            if (skill.includes('java') || skill.includes('spring') || skill.includes('hibernate')) {
                              tagColor = 'blue'; // Java ecosystem
                            } else if (skill.includes('react') || skill.includes('angular') || skill.includes('vue') || 
                                      skill.includes('javascript') || skill.includes('node') || skill.includes('html') || 
                                      skill.includes('css')) {
                              tagColor = 'green'; // Frontend/JS ecosystem
                            } else if (skill.includes('aws') || skill.includes('cloud') || skill.includes('docker') || 
                                      skill.includes('kubernetes') || skill.includes('devops')) {
                              tagColor = 'volcano'; // Cloud/DevOps
                            } else if (skill.includes('sql') || skill.includes('database') || skill.includes('oracle') || 
                                      skill.includes('mysql') || skill.includes('mongo')) {
                              tagColor = 'purple'; // Databases
                            } else if (skill.includes('agile') || skill.includes('scrum') || skill.includes('jira') || 
                                      skill.includes('management')) {
                              tagColor = 'orange'; // Process/Management
                            }
                            
                            return (
                              <Tag 
                                key={index} 
                                color={tagColor} 
                                style={{ 
                                  padding: '6px 10px', 
                                  borderRadius: '16px', 
                                  fontSize: '13px',
                                  margin: '4px'
                                }}
                              >
                                {interest.Interest[0]}
                              </Tag>
                            );
                          })}
                          {(!candidateDetails?.Interests?.[0]?.ExtInterest || candidateDetails.Interests[0].ExtInterest.length === 0) && (
                            <Text type="secondary">No skills information available</Text>
                          )}
                        </div>
                      </Card>
                    </ProfileSection>

                    <Divider style={{ margin: '16px 0' }} />

                    {/* Additional Information */}
                    <ProfileSection title="Additional Information">
                      <Card
                        style={{
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}
                      >
                        <Row gutter={[16, 16]}>
                          <Col xs={24} sm={12}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <CalendarOutlined style={{ color: '#1890ff', marginRight: '8px', fontSize: '16px' }} />
                              <div>
                                <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Experience</Text>
                                <Text strong>
                                  {candidateDetails?.ExperienceMonths?.[0]
                                    ? `${Math.floor(candidateDetails.ExperienceMonths[0] / 12)} years ${candidateDetails.ExperienceMonths[0] % 12} months`
                                    : 'Not specified'}
                                </Text>
                              </div>
                            </div>
                          </Col>
                          <Col xs={24} sm={12}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <BookOutlined style={{ color: '#1890ff', marginRight: '8px', fontSize: '16px' }} />
                              <div>
                                <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Highest Degree</Text>
                                <Text strong>{candidateDetails?.HighestDegree?.[0] || 'Not specified'}</Text>
                              </div>
                            </div>
                          </Col>
                          <Col xs={24} sm={12}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <EnvironmentOutlined style={{ color: '#1890ff', marginRight: '8px', fontSize: '16px' }} />
                              <div>
                                <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Willing to Relocate</Text>
                                <Text strong>{candidateDetails?.SFWillingToRelocate?.[0] === 'true' ? 'Yes' : 'No'}</Text>
                              </div>
                            </div>
                          </Col>
                          <Col xs={24} sm={12}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <BankOutlined style={{ color: '#1890ff', marginRight: '8px', fontSize: '16px' }} />
                              <div>
                                <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Current Employment</Text>
                                <Text strong>{candidateDetails?.CurrentlyEmployed?.[0] || 'Not specified'}</Text>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </Card>
                    </ProfileSection>

                    <Divider style={{ margin: '16px 0' }} />

                    {/* Action History */}
                    <ProfileSection title="Action History">
                      <Card
                        style={{
                          borderRadius: '8px',
                          backgroundColor: '#f9f9f9'
                        }}
                      >
                        <Text>
                          {candidateDetails?.ActionType?.[0] === 'VIEWED'
                            ? <><CalendarOutlined style={{ marginRight: '8px' }} /> You Viewed Profile • {new Date(candidateDetails?.LastActivity?.[0]).toLocaleDateString()}</>
                            : 'No recent activity'}
                        </Text>
                      </Card>
                    </ProfileSection>
                  </div>
                </TabPane>

                <TabPane 
                  tab={
                    <span>
                      <FileTextOutlined />
                      Full Resume
                    </span>
                  } 
                  key="fullResume"
                >
                  {renderResumeContent()}
                </TabPane>
                
                <TabPane 
                  tab={
                    <span>
                      <EditOutlined />
                      Notes
                    </span>
                  } 
                  key="notes"
                >
                  <Card 
                    title={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <EditOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
                        <span>Candidate Notes</span>
                      </div>
                    }
                    className="mb-6"
                    style={{
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.09)'
                    }}
                  >
                    <Row gutter={24} className="mb-5">
                      <Col span={24}>
                        <Notes
                          handleCancel={() => setShowNotes(false)}
                          isVisible={showNotes}
                          handleNotes={handleNotes}
                          notes={{
                            data: {
                              data: {
                                multipleNotes: candidateDetails?.notes || []
                              }
                            }
                          }}
                          getNotes={() => {}} // No need to fetch notes as we already have them
                          jobOpenings={candidateDetails}
                          user={users}
                          updateNoteState={setShowNotes}
                          handleUpdateNote={handleUpdateNotes}
                          handleDeleteNote={handleDeleteNotes}
                          notesField={"multipleNotes"}
                          isModal={false}
                        />
                      </Col>
                    </Row>
                  </Card>
                </TabPane>
              </Tabs>
            </Col>
          </Row>
        </div>
      )}
    </Drawer>
  )
}

// Add these styles to your CSS
const styles = `
.resume-content div {
  position: static !important;
  margin-bottom: 8px;
}

.resume-content {
  font-family: 'Times New Roman', serif;
  line-height: 1.5;
}

.resume-content b {
  font-weight: 600;
}
`;

// Add these styles to your component or CSS file
const resumeStyles = {
  resumeContent: {
    fontFamily: 'Calibri, Arial, sans-serif',
    lineHeight: '1.6',
    fontSize: '14px',
  },
  heading: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '12px',
  },
  section: {
    marginBottom: '20px',
  },
  listItem: {
    marginBottom: '8px',
  },
};
